import { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Typography,
  InputAdornment,
  TextField,
  Stack,
  Autocomplete,
  Box,
  TextareaAutosize,
  Button,
  DialogContent,
  DialogActions,
} from "@common-ui/ui";

import Address from "../../org/Address";

import { countries } from "../../../services";
import { removeNullAndUndefinedFromObject } from "../../../utils/common";

const CreatePlaceDialog = ({
  place,
  parent,
  handleAddNewPlace,
  setMode,
  addresses,
  handleClose,
}) => {
  const formik = useFormik({
    initialValues: {
      title: place && place.name ? place.name : "",
      address: null,
      address_line_1:
        place && place?.address?.address_line_1
          ? place?.address?.address_line_1
          : "",
      address_line_2:
        place && place?.address?.address_line_2
          ? place?.address?.address_line_2
          : "",
      city: place && place?.address?.city ? place?.address?.city : "",
      state:
        place && place?.address?.state_or_province
          ? place?.address?.state_or_province
          : "",
      country:
        place && place?.address?.country_or_region
          ? countries.find(
              (country) => country.label === place?.address?.country_or_region
            )
          : null,
      postal_code:
        place && place?.address?.postal_code ? place?.address?.postal_code : "",
      name: place && place.contact_name ? place.contact_name : "",
      phone: place && place.contact_phone ? place.contact_phone : "",
      email: place && place.contact_email ? place.contact_email : "",
      notes: place && place.notes ? place.notes : "",
    },
    validationSchema: Yup.object({
      title: Yup.string().trim().required(),
      address_line_1: Yup.string().trim(),
      address_line_2: Yup.string().trim(),
      city: Yup.string().trim(),
      state: Yup.string().trim(),
      country: Yup.object().nullable(),
      postal_code: Yup.string().trim(),
      name: Yup.string().trim(),
      phone: Yup.string()
        .nullable()
        .trim()
        .matches(/^[0-9]*$/),
      email: Yup.string().email().trim(),
      notes: Yup.string().trim(),
    }),
    onSubmit: () => {
      let {
        title,
        address_line_1,
        address_line_2,
        city,
        state,
        country,
        postal_code,
        name,
        phone,
        email,
        notes,
      } = formik.values;

      let attributes = {
        parent: parent ? parent : null,
        name: title,
        address: {
          address_line_1: address_line_1 ? address_line_1 : null,
          address_line_2: address_line_2 ? address_line_2 : null,
          city: city ? city : null,
          state_or_province: state ? state : null,
          country_or_region: country ? country.label : null,
          postal_code: postal_code ? postal_code : null,
        },
        contact_name: name ? name : null,
        contact_phone: phone ? phone : null,
        contact_email: email ? email : null,
        notes: notes ? notes : null,
      };

      if (
        Object.values(attributes.address).every(
          (val) => val === null || val === ""
        )
      ) {
        attributes.address = null;
      }

      attributes = removeNullAndUndefinedFromObject(attributes);
      handleAddNewPlace(attributes);
    },
  });

  useEffect(() => {
    if (formik.values.address) {
      formik.setFieldValue(
        "address_line_1",
        formik.values.address.address_line_1
      );
      formik.setFieldValue(
        "address_line_2",
        formik.values.address.address_line_2
      );
      formik.setFieldValue("city", formik.values.address.city);
      formik.setFieldValue("state", formik.values.address.state_or_province);
      formik.setFieldValue(
        "country",
        countries.find(
          (country) => country.label === formik.values.address.country_or_region
        )
      );
      formik.setFieldValue("postal_code", formik.values.address.postal_code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.address]);

  const getCurrentAddresses = () =>
    addresses && addresses.length
      ? addresses.filter(
          (address) => address.address_line_1 && address.address_line_1 !== " "
        )
      : [];

  return (
    <>
      <DialogContent dividers={true}>
        <div style={{ minWidth: 600 }}>
          <Grid item md={6} xs={12} sx={{ mb: 2 }}>
            <div>
              <Typography variant="subtitle2">
                <svg
                  width="16px"
                  height="16px"
                  viewBox="0 0 16 16"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g fillRule="evenodd">
                    <polygon
                      id="Shape"
                      fill="#EE2E24"
                      fillRule="nonzero"
                      points="7.17184265 4 8.82815735 4 8.5010352 7.164 11.1718427 5.308 12 6.692 9.00207039 8 12 9.308 11.1718427 10.692 8.5010352 8.836 8.82815735 12 7.17184265 12 7.4989648 8.836 4.82815735 10.692 4 9.308 6.99792961 8 4 6.692 4.82815735 5.308 7.4989648 7.164"
                    ></polygon>
                  </g>
                </svg>
                Place Title
              </Typography>
              <TextField
                sx={{ mt: 1 }}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {/* <Email /> */}
                    </InputAdornment>
                  ),
                }}
                autoComplete="off"
                error={Boolean(formik.touched.title && formik.errors.title)}
                fullWidth
                helperText={formik.touched.title && formik.errors.title}
                placeholder="Type"
                margin="normal"
                name="title"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                type="text"
                value={formik.values.title}
                variant="outlined"
              />
            </div>
          </Grid>

          <Stack direction="row" spacing={2}>
            <Grid item md={12} xs={12}>
              <Stack direction="column">
                <Typography sx={{ my: 2 }} color="textPrimary" variant="h4">
                  Address Information
                </Typography>
                <div style={{ marginBottom: 8 }}>
                  <Typography variant="subtitle2">Address</Typography>
                  <Autocomplete
                    id="address-select"
                    sx={{ mt: 1 }}
                    disablePortal
                    ListboxProps={{ style: { maxHeight: "200px" } }}
                    options={getCurrentAddresses()}
                    autoHighlight
                    size="small"
                    getOptionLabel={(option) => option?.address_line_1}
                    margin="normal"
                    name="address"
                    limitTags={4}
                    onBlur={formik.handleBlur}
                    onChange={(value, newValue) => {
                      formik.setFieldValue("address", newValue);
                    }}
                    fullWidth
                    value={formik.values.address}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                        {...props}
                      >
                        {`${option?.type} - ${option?.address_line_1}`}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select"
                        helperText={
                          formik.touched.address && formik.errors.address
                        }
                        error={Boolean(
                          formik.touched.address && formik.errors.address
                        )}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: "off", // disable autocomplete and autofill
                        }}
                      />
                    )}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Typography variant="subtitle2">Address 1</Typography>
                  <Address formik={formik} field="address_line_1" />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Typography variant="subtitle2">Address 2</Typography>
                  <TextField
                    autoComplete="off"
                    size="small"
                    error={Boolean(
                      formik.touched.address_line_2 &&
                        formik.errors.address_line_2
                    )}
                    fullWidth
                    helperText={
                      formik.touched.address_line_2 &&
                      formik.errors.address_line_2
                    }
                    placeholder="Type"
                    margin="normal"
                    sx={{ mt: 1 }}
                    name="address_line_2"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.address_line_2}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Typography variant="subtitle2">City</Typography>
                  <Address formik={formik} field="city" />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Typography variant="subtitle2">State/Province</Typography>
                  <Address formik={formik} field="state" />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Typography variant="subtitle2">Country / Region</Typography>
                  <Autocomplete
                    id="country-select"
                    sx={{ mt: 1 }}
                    disablePortal
                    ListboxProps={{ style: { maxHeight: "200px" } }}
                    options={countries}
                    autoHighlight
                    size="small"
                    getOptionLabel={(option) => option.label}
                    margin="normal"
                    name="country"
                    limitTags={4}
                    onBlur={formik.handleBlur}
                    onChange={(value, newValue) => {
                      formik.setFieldValue("country", newValue);
                    }}
                    fullWidth
                    value={formik.values.country}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                        {...props}
                      >
                        <img
                          loading="lazy"
                          width="20"
                          src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                          srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                          alt=""
                        />
                        {option.label} ({option.code}) +{option.phone}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select"
                        helperText={
                          formik.touched.country && formik.errors.country
                        }
                        error={Boolean(
                          formik.touched.country && formik.errors.country
                        )}
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: "off", // disable autocomplete and autofill
                        }}
                      />
                    )}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Typography variant="subtitle2">Zip/Postal Code</Typography>
                  <Address formik={formik} field="postal_code" />
                </div>
              </Stack>
            </Grid>

            <Grid item md={12} xs={12}>
              <Stack direction="column">
                <Typography sx={{ my: 2 }} color="textPrimary" variant="h4">
                  Contact Information
                </Typography>
                <div>
                  <Typography variant="subtitle2">Name</Typography>
                  <TextField
                    autoComplete="off"
                    size="small"
                    error={Boolean(formik.touched.name && formik.errors.name)}
                    fullWidth
                    helperText={formik.touched.name && formik.errors.name}
                    placeholder="Type"
                    margin="normal"
                    sx={{ mt: 1 }}
                    name="name"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.name}
                  />
                </div>

                <div
                //  style={{ marginBottom: 8 }}
                >
                  <Typography variant="subtitle2">Phone</Typography>
                  <TextField
                    autoComplete="off"
                    size="small"
                    error={Boolean(formik.touched.phone && formik.errors.phone)}
                    fullWidth
                    helperText={formik.touched.phone && formik.errors.phone}
                    placeholder="Type"
                    margin="normal"
                    sx={{ mt: 1 }}
                    name="phone"
                    type="string"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.phone}
                  />
                </div>

                <div
                // style={{ marginBottom: 8 }}
                >
                  <Typography variant="subtitle2">Email</Typography>
                  <TextField
                    autoComplete="off"
                    size="small"
                    error={Boolean(formik.touched.email && formik.errors.email)}
                    fullWidth
                    helperText={formik.touched.email && formik.errors.email}
                    placeholder="Type"
                    margin="normal"
                    sx={{ mt: 1 }}
                    name="email"
                    type="email"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.email}
                  />
                </div>
              </Stack>
            </Grid>
          </Stack>

          <Grid item md={6} xs={12} sx={{ mb: 2 }}>
            <div>
              <Typography variant="subtitle2">Notes</Typography>
              <TextField
                sx={{ mt: 1 }}
                size="small"
                error={Boolean(formik.touched.notes && formik.errors.notes)}
                fullWidth
                helperText={formik.touched.notes && formik.errors.notes}
                placeholder="Type"
                margin="normal"
                onChange={formik.handleChange}
                value={formik.values.notes}
                onBlur={formik.handleBlur}
                InputProps={{
                  inputComponent: TextareaAutosize,
                  inputProps: { minRows: 4 },
                }}
                name="notes"
                variant="outlined"
              />
            </div>
          </Grid>
        </div>
      </DialogContent>
      <DialogActions
        sx={
          {
            // justifyContent: "space-between",
          }
        }
      >
        {/* <Button onClick={props.handleClose} variant="contained">
              Ok
            </Button> */}

        <div>
          <Button
            color="primary"
            // disabled={formik.isSubmitting}
            fullWidth
            size="large"
            // onClick={() => router.goBack()}
            onClick={() => {
              handleClose();
              formik.handleReset();
            }}
            // type="submit"
            variant="text"
          >
            Cancel
          </Button>
        </div>
        <div>
          <Button
            color="primary"
            disabled={!(formik.isValid && formik.dirty && !formik.isSubmitting)}
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            onClick={formik.handleSubmit}
          >
            Save
          </Button>
        </div>
      </DialogActions>
    </>
  );
};

export default CreatePlaceDialog;
